import { BadgeCheck } from "lucide-react";

import { CihSectionHeader } from "~/components/cih/section-header";
import { StarRating } from "~/components/ui/star-rating";

import {
  getRemainingTestimonials,
  getTestimonialAggregate,
  type TestimonialItem,
} from "./featured-testimonial";

type Props = {
  testimonials: TestimonialItem[];
  sectionNumber: number;
};

export function CoachTestimonialsSection({
  testimonials,
  sectionNumber,
}: Props) {
  const items = getRemainingTestimonials(testimonials);
  const aggregate = getTestimonialAggregate(testimonials);
  if (items.length === 0) return null;

  return (
    <section id="testimonials" className="border-t border-gray-200 bg-gray-100">
      <CihSectionHeader
        label={`${String(sectionNumber).padStart(2, "0")} · Testimonials`}
        title="In their own words"
      />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        {aggregate && (
          <div className="mb-10 flex items-center gap-3">
            <StarRating value={Math.round(aggregate.average)} size="sm" />
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900 tabular-nums">
                {aggregate.average.toFixed(1)}
              </span>{" "}
              from {aggregate.count} verified client
              {aggregate.count === 1 ? "" : "s"}
            </p>
          </div>
        )}
        <div className="grid gap-10 md:grid-cols-3">
          {items.map((t) => (
            <figure key={t.id} className="border-t border-gray-200 pt-6">
              {t.source === "client" && t.rating != null && (
                <StarRating value={t.rating} size="sm" className="mb-3" />
              )}
              <blockquote className="text-base text-gray-900">
                {t.content}
              </blockquote>
              {t.author && (
                <figcaption className="mt-3 flex items-center gap-2 text-xs tracking-[0.14em] text-gray-500 uppercase">
                  {t.author}
                  {t.source === "client" && (
                    <span className="inline-flex items-center gap-1 font-medium tracking-normal text-blue-600 normal-case">
                      <BadgeCheck className="size-3.5" />
                      Verified client
                    </span>
                  )}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
